import React, { useEffect, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash'
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import { transparentize } from 'polished';
import maps from '../../../shared/helpers/world';
import { setCurrencyFormat } from '../../../utils/helpers';

// you can find demos here: https://www.amcharts.com/demos

const strokeColor = am4core.color("#AAAA00");
const mapMainColor = am4core.color("#AAAA00");
const lightGrayColor = '#F2F2F2';
const lightTextColor = '#93A2AF';
const whiteColor = '#FFFFFF';


const RegionHeatMap = ({ mapData }) => {
    const { t } = useTranslation('common');
    const dataRef = useRef({ mapData: [] });


    const createMap = () => {
        const map = am4core.create('chartdiv', am4maps.MapChart);

        map.geodata = maps;

        map.centerMapOnZoomOut = false;
        // Set projection
        map.projection = new am4maps.projections.Miller();

        const polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
        // Add heat rule
        polygonSeries.heatRules.push({
            "property": "fill",
            "target": polygonSeries.mapPolygons.template,
            "min": am4core.color("#ffffff"),
            "max": am4core.color("#AAAA00"),
            "logarithmic": true
        });

        polygonSeries.useGeodata = true;
        const label = map.createChild(am4core.Label);
        label.text = t('sales_by_region');
        label.fontSize = 12;
        label.align = 'center';
        label.valign = 'bottom';
        label.fill = am4core.color(lightTextColor);
        label.padding(0, 0, 50, 0);
        const noDataLabel = map.createChild(am4core.Label);
        noDataLabel.text = t('no_available_data');
        noDataLabel.fontSize = 12;
        noDataLabel.align = 'center';
        noDataLabel.valign = 'middle';
        noDataLabel.padding(10, 20, 10, 20);
        noDataLabel.background.fill = am4core.color(lightGrayColor);
        noDataLabel.background.stroke = am4core.color('#000000');
        noDataLabel.fillOpacity = mapData === null || mapData.length === 0 ? 1 : 0;
        noDataLabel.background.strokeOpacity = mapData === null || mapData.length === 0 ? 1 : 0;
        const heatLegend = map.createChild(am4maps.HeatLegend);
        heatLegend.series = polygonSeries;
        heatLegend.padding(0, 20, 12, 20);
        heatLegend.width = 300;
        heatLegend.valueAxis.renderer.minGridDistance = 60;
        heatLegend.valueAxis.numberFormatter.numberFormat = '#.a';
        heatLegend.valueAxis.renderer.labels.template.adapter.add(
            'text', text => (mapData && mapData.length > 0 ? text : ''),
        );
        heatLegend.align = 'center';
        heatLegend.valign = 'bottom';
        heatLegend.minColor = '#FFF';
        heatLegend.maxColor = mapMainColor;
        heatLegend.valueAxis.tooltip.background.fill = am4core.color(whiteColor);
        heatLegend.valueAxis.tooltip.background.stroke = am4core.color(mapMainColor);
        heatLegend.valueAxis.tooltip.label.fill = am4core.color('#000');
        heatLegend.stroke = am4core.color(lightTextColor);
        heatLegend.valueAxis.strokeWidth = 0;

        const polygonTemplate = polygonSeries.mapPolygons.template;

        let minRange = heatLegend.valueAxis.axisRanges.create();
        minRange.label.paddingTop = -15;

        let maxRange = heatLegend.valueAxis.axisRanges.create();
        maxRange.label.paddingTop = -15;

        heatLegend.valueAxis.renderer.labels.template.adapter.add('text', () => (''));

        polygonSeries.events.on('datavalidated', () => {
            const min = heatLegend.series.dataItem.values.value.low;
            minRange = heatLegend.valueAxis.axisRanges.getIndex(0);
            minRange.value = min;
            minRange.label.text = `${min}`;

            const max = heatLegend.series.dataItem.values.value.high;
            maxRange = heatLegend.valueAxis.axisRanges.getIndex(1);
            maxRange.value = max;
            maxRange.label.text = `${heatLegend.numberFormatter.format(max)}`;

            minRange.label.fill = am4core.color(lightTextColor);
            maxRange.label.fill = am4core.color(lightTextColor);

            if (min === max) {
                minRange.label.paddingRight = 300;
                maxRange.label.paddingLeft = 300;
            } else {
                minRange.label.paddingRight = 45;
                maxRange.label.paddingLeft = 45;
            }
        });

        polygonTemplate.nonScalingStroke = true;
        polygonTemplate.strokeWidth = 0.5;
        polygonTemplate.fill = am4core.color('#f7f7f7');
        polygonTemplate.stroke = am4core.color('#999999');

        const tooltipFormatter = (target) => {
            const { dataContext } = target.dataItem;
            return `${dataContext?.countryName ?? dataContext.name}\n
            Amount: ${dataContext.value ? setCurrencyFormat(Number(dataContext.value).toFixed(2), true) : 0}\n
            ${dataContext.state ? `State ${dataContext.state ? dataContext.state : 'N/A'}\n` : ''}
            Region: ${dataContext.region ? dataContext.region : dataContext['region-wb']}\n
            SubRegion:${dataContext.subRegion ? dataContext.subRegion : dataContext.subregion}\n`;
        };

        polygonSeries.data = mapData ? [...mapData] : [];
        polygonTemplate.adapter.add('tooltipText', (text, target) => tooltipFormatter(target));
        polygonSeries.tooltip.getFillFromObject = false;
        polygonSeries.tooltip.background.fill = am4core.color(`${transparentize(0.2, '#FFF')}`);
        polygonSeries.tooltip.label.fill = am4core.color('#000');

        const handleHover = (mapPolygon) => {

            // eslint-disable-next-line no-restricted-globals
            if (!isNaN(mapPolygon.dataItem.value)) {
                heatLegend.valueAxis.showTooltipAt(mapPolygon.dataItem.value);
            } else {
                heatLegend.valueAxis.hideTooltip();
            }
        };

        polygonSeries.mapPolygons.template.events.on('over', (event) => {
            handleHover(event.target);
        });

        polygonSeries.mapPolygons.template.events.on('hit', (event) => {
            handleHover(event.target);
        });

        polygonSeries.mapPolygons.template.events.on('out', () => {
            heatLegend.valueAxis.hideTooltip();
        });

        const hs = polygonTemplate.states.create('hover');
        hs.properties.stroke = am4core.color(strokeColor);
        hs.properties.strokeWidth = 2;
        const zoomControl = new am4maps.ZoomControl();
        zoomControl.align = 'left';
        zoomControl.valign = 'middle';
        map.zoomControl = zoomControl;
    }

    useEffect(() => {

        if (!dataRef.current.mapData) {
            dataRef.current.mapData = mapData;
            createMap()
        } else if (!isEqual(mapData, dataRef.current.mapData)) {
            createMap()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapData]);

    return (<div className="row align-items-center justify-content-center">
        {mapData.length ? <> <div id="chartdiv" style={{ width: '100%', height: '55vh' }} />
            <div className="col-12 text-right"><small className="pl-2 col" >
                (* City,State Type selection criteria is not applicable
                for this Heat-Map Graph.)</small></div></>
            : <div>No Data Available</div>}
    </div>

    );
};

RegionHeatMap.propTypes = {
    mapData: PropTypes.arrayOf(PropTypes.shape()),
};

RegionHeatMap.defaultProps = {
    mapData: [],
};

export default memo(RegionHeatMap);
